import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} StickyServicesHeadingRefs
 * @property {HTMLElement} heading - The services heading element
 * @property {HTMLElement} placeholder - The placeholder element
 */

/**
 * Sticky Services Heading Component
 *
 * Creates sticky behavior for services-heading elements within custom-grid-section containers.
 * The heading becomes fixed when it reaches the header height and stays fixed until
 * the bottom of its parent section reaches the header.
 *
 * @extends {Component<StickyServicesHeadingRefs>}
 */
class StickyServicesHeading extends Component {
  /** @type {number|null} */
  #scrollAnimationFrame = null;

  /** @type {number|null} */
  #resizeTimeout = null;

  /** @type {number} */
  #originalTop = 0;

  /** @type {HTMLElement|null} */
  #section = null;

  /** @type {boolean} */
  #isSticky = false;

  /** @type {boolean} */
  #isAtBottom = false;

  /** @type {IntersectionObserver|null} */
  #intersectionObserver = null;

  connectedCallback() {
    super.connectedCallback();

    // Find the parent .custom-grid-section
    this.#section = this.closest('.custom-grid-section');

    if (!this.#section || !this.refs.heading) {
      return;
    }

    // Store original position and set placeholder height
    this.#storeOriginalPosition();

    // Observe when section leaves viewport (so we unstick even if last scroll frame missed it)
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry || entry.isIntersecting) return;
        if (entry.boundingClientRect.bottom <= 0) {
          this.#updateStickyState();
        }
      },
      { root: null, rootMargin: '0px', threshold: 0 }
    );
    this.#intersectionObserver.observe(this.#section);

    // Listen for scroll and resize events
    window.addEventListener('scroll', this.#handleScroll, { passive: true });
    window.addEventListener('resize', this.#handleResize, { passive: true });

    // Initial update
    this.#updateStickyState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('scroll', this.#handleScroll);
    window.removeEventListener('resize', this.#handleResize);

    if (this.#scrollAnimationFrame) {
      cancelAnimationFrame(this.#scrollAnimationFrame);
      this.#scrollAnimationFrame = null;
    }

    if (this.#resizeTimeout) {
      clearTimeout(this.#resizeTimeout);
      this.#resizeTimeout = null;
    }

    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;

    if (this.refs.heading) {
      delete this.refs.heading.dataset.stickyHeading;
      document.dispatchEvent(new CustomEvent(ThemeEvents.stickyHeadingExited, { bubbles: true }));
    }
  }

  /**
   * Stores the original top position of the heading
   */
  #storeOriginalPosition() {
    if (!this.refs.heading) return;

    // Remove sticky classes to get accurate position
    this.refs.heading.classList.remove('is-sticky', 'is-at-bottom');

    const rect = this.refs.heading.getBoundingClientRect();
    this.#originalTop = rect.top + window.scrollY;

    // Set placeholder height
    if (this.refs.placeholder) {
      this.refs.placeholder.style.setProperty('--services-heading-height', `${this.refs.heading.offsetHeight}px`);
    }
  }

  /**
   * Gets the current header height from CSS variable
   * @returns {number}
   */
  #getHeaderHeight() {
    // --header-group-height is defined on body, not :root
    const headerHeightVar = getComputedStyle(document.body).getPropertyValue('--header-group-height').trim();
    return parseFloat(headerHeightVar) || 0;
  }

  /**
   * Updates the sticky state based on scroll position
   */
  #updateStickyState() {
    if (!this.refs.heading || !this.#section) return;

    const headerHeight = this.#getHeaderHeight();
    const sectionRect = this.#section.getBoundingClientRect();
    const headingHeight = this.refs.heading.offsetHeight;
    const scrollY = window.scrollY;

    // Distance from original position to where it should become sticky
    const stickyTrigger = this.#originalTop - headerHeight;

    // When the section bottom reaches the header + heading height, stop being sticky
    const sectionBottomFromTop = sectionRect.bottom;
    const stopStickyAt = headerHeight + headingHeight;

    // Section has scrolled completely above viewport, unstick so header can go transparent
    const sectionStillInView = sectionRect.bottom > 0;

    // Should the heading be sticky?
    const shouldBeSticky = scrollY >= stickyTrigger && sectionStillInView;

    // Should the heading be at the bottom of its section?
    const isAtBottom = sectionBottomFromTop <= stopStickyAt;

    // Apply states
    if (isAtBottom && shouldBeSticky) {
      if (!this.#isAtBottom) {
        this.#isAtBottom = true;
        this.#isSticky = false;
        this.refs.heading.classList.remove('is-sticky');
        this.refs.heading.classList.add('is-at-bottom');
        this.refs.heading.dataset.stickyHeading = 'true';
        document.dispatchEvent(new CustomEvent(ThemeEvents.stickyHeadingEntered, { bubbles: true }));

        // Calculate position so heading appears at bottom of section
        const topPosition = sectionBottomFromTop - headingHeight;
        this.refs.heading.style.setProperty('--sticky-top-position', `${topPosition}px`);
      } else {
        // Update position as section scrolls
        const topPosition = sectionBottomFromTop - headingHeight;
        this.refs.heading.style.setProperty('--sticky-top-position', `${topPosition}px`);
      }
    } else if (shouldBeSticky) {
      if (!this.#isSticky || this.#isAtBottom) {
        this.#isSticky = true;
        this.#isAtBottom = false;
        this.refs.heading.classList.add('is-sticky');
        this.refs.heading.classList.remove('is-at-bottom');

        // Heading is now at header bottom - apply header background
        this.refs.heading.dataset.stickyHeading = 'true';
        document.dispatchEvent(new CustomEvent(ThemeEvents.stickyHeadingEntered, { bubbles: true }));
      }
    } else {
      if (this.#isSticky || this.#isAtBottom) {
        this.#isSticky = false;
        this.#isAtBottom = false;
        this.refs.heading.classList.remove('is-sticky', 'is-at-bottom');
        delete this.refs.heading.dataset.stickyHeading;
        document.dispatchEvent(new CustomEvent(ThemeEvents.stickyHeadingExited, { bubbles: true }));
      }
    }
  }

  /**
   * Handles scroll events with throttling
   */
  #handleScroll = () => {
    if (this.#scrollAnimationFrame) return;

    this.#scrollAnimationFrame = requestAnimationFrame(() => {
      if (!this.isConnected) {
        this.#scrollAnimationFrame = null;
        return;
      }

      this.#updateStickyState();
      this.#scrollAnimationFrame = null;
    });
  };

  /**
   * Handles resize events with debouncing
   */
  #handleResize = () => {
    if (this.#resizeTimeout) {
      clearTimeout(this.#resizeTimeout);
    }

    this.#resizeTimeout = setTimeout(() => {
      if (!this.isConnected) return;

      // Recalculate original position on resize
      this.#storeOriginalPosition();
      this.#updateStickyState();
      this.#resizeTimeout = null;
    }, 100);
  };
}

customElements.define('sticky-services-heading', StickyServicesHeading);
